import MirrorBackButton from "@/components/MirrorBackButton";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChevronLeft, Swords, Clock, Brain, Archive, Award,
  Trophy, Star, ArrowRight, Lock, Check, ChevronDown, ChevronUp,
} from "lucide-react";
import MobileLayout from "@/components/MobileLayout";
import { useLanguage } from "@/i18n/LanguageProvider";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import { useChallenges } from "@/hooks/useChallenges";

/* ── helpers ──────────────────────────────────────── */
const tpl = (s: string, vars: Record<string, string | number>) => {
  let out = s;
  for (const [k, v] of Object.entries(vars)) out = out.replace(`{${k}}`, String(v));
  return out;
};

/* ── Page ──────────────────────────────────────────── */
const MirrorChallenge = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const [showAllBadges, setShowAllBadges] = useState(false);
  const {
    activeChallenge,
    pastChallenges,
    badges,
    loading,
    logDay,
    getDaysRemaining,
    refresh,
  } = useChallenges(t);

  // Refresh when returning from check-in
  useEffect(() => {
    if (location.state?.fromCheckin) {
      refresh();
      // Clear the state so it doesn't re-trigger
      window.history.replaceState({}, "");
    }
  }, [location.state, refresh]);

  const stagger = (i: number) => ({ delay: 0.06 + i * 0.07, duration: 0.5 });

  const handleLogDay = async (dayIndex: number) => {
    if (!activeChallenge) return;
    if (activeChallenge.checkpoints[dayIndex]?.completed) return;

    const newProgress = activeChallenge.checkpoints.filter((cp, i) => cp.completed || i === dayIndex).length;
    await logDay(activeChallenge.id, dayIndex);
    toast.success(t("challenge.dayLogged"));

    if (newProgress >= activeChallenge.targetValue) {
      setTimeout(() => {
        toast.success(tpl(t("challenge.challengeComplete"), { pts: activeChallenge.rewardPoints }));
      }, 600);
    }
  };

  const daysLeft = activeChallenge ? getDaysRemaining(activeChallenge) : 0;

  return (
    <MobileLayout className="pb-28 pt-6">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="flex items-center gap-3 mb-1">
        <MirrorBackButton />
        <div className="flex items-center gap-2">
          <Swords size={15} className="text-primary" />
          <p className="text-[9px] font-display tracking-[0.22em] uppercase text-primary/60">
            {t("challenge.subtitle")}
          </p>
        </div>
      </motion.div>
      <motion.h1 initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05 }}
        className="font-display text-[26px] font-semibold text-foreground leading-none mb-6">
        {t("challenge.title")}
      </motion.h1>

      {loading && (
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      )}

      {/* ── EMPTY STATE ──────────────────────────────── */}
      {!loading && !activeChallenge && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="vyv-card p-5 mb-6 text-center">
          <p className="text-foreground/50 text-[12px] font-body leading-relaxed">
            {t("challenge.noActive")}
          </p>
          <button onClick={() => navigate("/check-in", { state: { fromChallenge: true } })}
            className="mt-4 flex items-center justify-center gap-2 mx-auto py-2.5 px-5 rounded-xl bg-primary/10 border border-primary/15 text-primary/70 text-[12px] font-display font-medium hover:bg-primary/15 transition-colors">
            {t("checkin.title")} <ArrowRight size={13} />
          </button>
        </motion.div>
      )}

      {/* ── ACTIVE CHALLENGE HERO ─────────────────── */}
      {!loading && activeChallenge && (() => {
        const completedDays = activeChallenge.checkpoints.filter(cp => cp.completed).length;
        const progressPct = (completedDays / activeChallenge.targetValue) * 100;
        const badge = badges.find(b => b.badgeKey === activeChallenge.rewardBadgeId);
        const ringR = 46;
        const ringC = 2 * Math.PI * ringR;

        return (
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={stagger(1)}
            className="rounded-2xl p-5 mb-6"
            style={{
              background: "linear-gradient(135deg, #0d1117 0%, #1a1a2e 100%)",
              border: "1px solid rgba(0,229,255,0.4)",
              boxShadow: "0 0 25px rgba(0,229,255,0.1)",
            }}>
            {/* Top badges */}
            <div className="flex items-center justify-between mb-3">
              <span className="text-[9px] font-display tracking-[0.2em] uppercase text-primary/55">
                {tpl(t("challenge.weekLabel"), { n: activeChallenge.weekNumber })}
              </span>
              <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-500/10 border border-yellow-500/20">
                <Clock size={9} className="text-yellow-400" />
                <span className="text-[9px] font-display font-medium text-yellow-400">
                  {daysLeft} {t("challenge.daysLeft")}
                </span>
              </div>
            </div>

            {/* Progress Ring */}
            <div className="flex items-center gap-5 mb-4">
              <div className="relative shrink-0" style={{ width: 100, height: 100 }}>
                <svg width="100" height="100" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r={ringR} fill="none" stroke="hsl(222 18% 15%)" strokeWidth="5" />
                  <motion.circle cx="50" cy="50" r={ringR} fill="none" stroke="url(#challengeRingGrad)" strokeWidth="5"
                    strokeLinecap="round" strokeDasharray={ringC}
                    initial={{ strokeDashoffset: ringC }}
                    animate={{ strokeDashoffset: ringC * (1 - progressPct / 100) }}
                    transition={{ delay: 0.3, duration: 1.2, ease: "easeOut" }}
                    transform="rotate(-90 50 50)"
                    style={{ filter: "drop-shadow(0 0 6px hsl(187 72% 50% / 0.4))" }}
                  />
                  <defs>
                    <linearGradient id="challengeRingGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="hsl(187 72% 40%)" />
                      <stop offset="100%" stopColor="hsl(187 72% 60%)" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="font-display text-[24px] font-bold text-white leading-none">{Math.round(progressPct)}</span>
                  <span className="text-[8px] font-display tracking-wide text-primary/50">%</span>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-display text-[22px] font-bold text-foreground leading-tight mb-1">
                  {activeChallenge.title}
                </h2>
                <p className="text-foreground/45 text-[12px] font-body leading-relaxed mb-1.5">
                  {activeChallenge.description}
                </p>
                <span className="text-[10px] font-display text-foreground/50 tracking-wide">
                  {tpl(t("challenge.progressLabel"), { done: completedDays, total: activeChallenge.targetValue, unit: activeChallenge.progressUnit })}
                </span>
              </div>
            </div>

            {/* This Week check visual */}
            <div className="mb-5">
              <p className="text-[9px] font-display tracking-[0.2em] uppercase text-primary/45 mb-3">
                {t("challenge.thisWeek")}
              </p>
              <div className="flex items-center justify-between px-1">
                {activeChallenge.checkpoints.map((cp, i) => {
                  const isToday = i === completedDays && !cp.completed;
                  return (
                    <button key={i} onClick={() => handleLogDay(i)}
                      className="flex flex-col items-center gap-1.5 group">
                      <motion.div
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                          cp.completed
                            ? "bg-primary/20 border border-primary/40"
                            : isToday
                              ? "border-2 border-primary/50 bg-transparent"
                              : "border border-border/20 bg-foreground/[0.03]"
                        }`}
                        style={isToday ? { boxShadow: "0 0 12px hsl(187 72% 50% / 0.25)" } : undefined}
                        whileTap={{ scale: 0.9 }}
                      >
                        {cp.completed ? (
                          <Check size={14} className="text-primary" />
                        ) : (
                          <div className={`w-2 h-2 rounded-full ${isToday ? "bg-primary/50" : "bg-foreground/15"}`} />
                        )}
                      </motion.div>
                      <span className={`text-[9px] font-display ${
                        cp.completed ? "text-primary/70" : isToday ? "text-primary/60 font-semibold" : "text-foreground/30"
                      }`}>
                        {tpl(t("challenge.day"), { n: i + 1 })}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Reward */}
            <div className="rounded-xl p-3 bg-foreground/[0.03] border border-border/15 mb-4">
              <div className="flex items-center gap-2 mb-1.5">
                <Trophy size={13} className="text-yellow-400" />
                <span className="text-[9px] font-display tracking-[0.18em] uppercase text-foreground/40">
                  {t("challenge.reward")}
                </span>
              </div>
              <p className="text-foreground/60 text-[11px] font-body leading-relaxed mb-1">
                {tpl(t("challenge.rewardDesc"), { pts: activeChallenge.rewardPoints })}
              </p>
              <span className="text-[10px] font-display px-2 py-0.5 rounded-full bg-yellow-500/8 border border-yellow-500/15 text-yellow-400/70">
                {badge
                  ? tpl(t("challenge.badgeUnlocks"), { emoji: badge.emoji, name: badge.title })
                  : ""}
              </span>
            </div>

            <button onClick={() => navigate("/mirror")}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-primary/25 text-primary/70 text-[12px] font-display font-medium hover:bg-primary/10 transition-colors">
              {t("challenge.talkToMirror")} <ArrowRight size={13} />
            </button>
          </motion.div>
        );
      })()}

      {/* ── WHY THIS CHALLENGE ────────────────────── */}
      {activeChallenge && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={stagger(2)}
          className="rounded-2xl p-4 mb-6"
          style={{ background: "hsl(var(--card))", border: "1px solid hsl(187 72% 50% / 0.12)" }}>
          <div className="flex items-start gap-2.5">
            <Brain size={14} className="text-primary/60 mt-0.5 shrink-0" />
            <p className="text-foreground/50 text-[12px] font-body leading-relaxed">
              {activeChallenge.reasoning}
            </p>
          </div>
        </motion.div>
      )}

      {/* ── PAST CHALLENGES ───────────────────────── */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={stagger(3)} className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Archive size={13} className="text-primary/60" />
          <p className="text-[9px] font-display tracking-[0.22em] uppercase text-primary/55">
            {t("challenge.pastChallenges")}
          </p>
        </div>
        {pastChallenges.length === 0 ? (
          <div className="vyv-card p-4 text-center">
            <p className="text-foreground/40 text-[11px] font-body">{t("challenge.noPast")}</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {pastChallenges.map((ch) => {
              const badge = badges.find(b => b.badgeKey === ch.rewardBadgeId);
              const isCompleted = ch.status === "completed";
              return (
                <div key={ch.id} className="vyv-card p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <span className="text-[9px] font-display tracking-wide text-muted-foreground uppercase">
                        {tpl(t("challenge.weekLabel"), { n: ch.weekNumber })}
                      </span>
                      <p className="font-display text-[13px] font-semibold text-foreground leading-snug mt-0.5">{ch.title}</p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {isCompleted ? (
                        <span className="flex items-center gap-1 text-[10px] font-display font-medium" style={{ color: "hsl(142 71% 45%)" }}>
                          <Check size={11} /> {t("challenge.completed")}
                        </span>
                      ) : (
                        <span className="text-[10px] font-display font-medium" style={{ color: "hsl(0 72% 51%)" }}>
                          {t("challenge.failed")}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-[11px] font-display font-semibold ${isCompleted ? "text-green-400" : "text-foreground/30"}`}>
                      {isCompleted ? tpl(t("challenge.pts"), { pts: ch.rewardPoints }) : t("challenge.noPts")}
                    </span>
                    {badge && (
                      <span className={`text-[10px] font-display px-2 py-0.5 rounded-full border ${
                        isCompleted
                          ? "bg-primary/8 border-primary/15 text-primary/60"
                          : "bg-foreground/[0.03] border-border/15 text-foreground/25"
                      }`}>
                        {isCompleted
                          ? tpl(t("challenge.badgeEarned"), { emoji: badge.emoji, name: badge.title })
                          : tpl(t("challenge.badgeLocked"), { name: badge.title })}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </motion.div>

      {/* ── YOUR BADGES ───────────────────────────── */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={stagger(4)} className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Award size={13} className="text-primary/60" />
          <p className="text-[9px] font-display tracking-[0.22em] uppercase text-primary/55">
            {t("challenge.badges")}
          </p>
        </div>
        <div className={`grid grid-cols-2 gap-2.5 mb-3 ${!showAllBadges ? "max-h-[200px] overflow-hidden" : ""}`}>
          {badges.map((b) => (
            <div key={b.id} className={`vyv-card p-3.5 flex items-center gap-3 ${!b.unlocked ? "opacity-40" : ""}`}>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-[18px] ${
                b.unlocked ? "bg-primary/10 border border-primary/15" : "bg-foreground/5 border border-border/15"
              }`}>
                {b.unlocked ? b.emoji : <Lock size={14} className="text-foreground/25" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`font-display text-[12px] font-semibold leading-snug truncate ${
                  b.unlocked ? "text-foreground" : "text-foreground/30"
                }`}>{t(`badge.${b.badgeKey}`) !== `badge.${b.badgeKey}` ? t(`badge.${b.badgeKey}`) : b.title}</p>
                {b.unlocked && b.relatedWeek && (
                  <p className="text-[9px] text-muted-foreground font-display">
                    {tpl(t("challenge.weekLabel"), { n: b.relatedWeek })}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="vyv-card p-3 text-center mb-3">
          <p className="text-foreground/40 text-[11px] font-body">{t("challenge.badgeCollectionInfo")}</p>
        </div>
        <button onClick={() => setShowAllBadges(prev => !prev)}
          className="w-full flex items-center justify-center gap-2 py-2 rounded-xl border border-primary/15 text-primary/60 text-[11px] font-display font-medium hover:bg-primary/5 transition-colors">
          {showAllBadges ? t("challenge.hideBadges") : t("challenge.viewAllBadges")}
          {showAllBadges ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
        </button>
      </motion.div>

      {/* ── MIRROR REFLECTION ─────────────────────── */}
      {/* HIDDEN: fake Mirror Insight — re-wire with real Padrão B per audit 23/04 (memory #23) */}
      {false && (
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={stagger(5)}
        className="rounded-2xl p-5 border border-primary/15"
        style={{ background: "linear-gradient(rgba(0,0,0,0.58), rgba(0,0,0,0.72)), url(https://lluvykhqgdnbfobjixbj.supabase.co/storage/v1/object/public/mirror-brief/room%20ex_05.jpeg) center/cover no-repeat" }}>
        <div className="flex items-start gap-2.5 mb-4">
          <Star size={13} className="text-primary/60 mt-0.5 shrink-0" />
          <p className="text-foreground/55 text-[12px] font-body leading-relaxed italic">
            "{t("challenge.mirrorReflection")}"
          </p>
        </div>
        <button onClick={() => navigate("/mirror")}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-primary/10 border border-primary/15 text-primary/70 text-[12px] font-display font-medium hover:bg-primary/15 transition-colors">
          {t("challenge.openMirror")} <ArrowRight size={13} />
        </button>
      </motion.div>
      )}

    </MobileLayout>
  );
};

export default MirrorChallenge;
